import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, isSessionError, formatRupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Th, Td } from "@/pages/admin/OrdersPanel";
import { FilterSelect, FilterInput, Pager, ExportButton, fmtWIB, cleanParams } from "@/components/admin/LogTools";

const STATUS = { paid: "Dibayar", completed: "Selesai", processing: "Diproses", cancelled: "Dibatalkan", rejected: "Ditolak", deleted: "Dihapus Otomatis" };
const STATUS_CLS = { completed: "text-emerald-400", paid: "text-sky-300", processing: "text-amber-300", cancelled: "text-rose-400", rejected: "text-rose-400", deleted: "text-zinc-500" };
const KIND = { topup: "Top Up", bongkar: "Bongkar" };
const EMPTY = { date_from: "", date_to: "", kind: "", product_id: "", status: "", source: "", staff: "", q: "" };

const Summary = ({ s }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="history-summary">
    <div className="rk-glass rounded-2xl p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total (sesuai filter)</div>
      <div className="mt-1 text-2xl font-extrabold" data-testid="summary-total-count">{s.total.count.toLocaleString("id-ID")} trx</div>
      <div className="text-sm text-[#ffd700]" data-testid="summary-total-rp">{formatRupiah(s.total.amount_rp)}</div>
      <div className="text-xs text-zinc-400" data-testid="summary-total-chip">{s.total.chip_b.toLocaleString("id-ID")} B chip</div>
    </div>
    {s.groups.map((g) => (
      <div key={`${g.kind}-${g.status}`} className="rounded-2xl border border-white/5 bg-[#0d0d12] p-4" data-testid={`summary-${g.kind}-${g.status}`}>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{KIND[g.kind]} · <span className={STATUS_CLS[g.status]}>{STATUS[g.status] || g.status}</span></div>
        <div className="mt-1 text-lg font-bold">{g.count.toLocaleString("id-ID")} trx</div>
        <div className="text-xs text-zinc-400">{formatRupiah(g.amount_rp)} · {g.chip_b.toLocaleString("id-ID")} B</div>
      </div>
    ))}
  </div>
);

const Filters = ({ f, set, staff, onReset }) => (
  <div className="flex flex-wrap items-end gap-3" data-testid="history-filters">
    <FilterInput label="Dari tanggal" type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} testid="filter-date-from" />
    <FilterInput label="Sampai tanggal" type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} testid="filter-date-to" />
    <FilterSelect label="Jenis" value={f.kind} onChange={(v) => set("kind", v)} options={Object.entries(KIND)} testid="filter-kind" />
    <FilterSelect label="Game" value={f.product_id} onChange={(v) => set("product_id", v)} options={[["royal_dream", "Royal Dream"], ["higgs_domino", "Higgs Domino"]]} testid="filter-game" />
    <FilterSelect label="Status" value={f.status} onChange={(v) => set("status", v)} options={Object.entries(STATUS)} testid="filter-status" />
    <FilterSelect label="Sumber" value={f.source} onChange={(v) => set("source", v)} options={[["member", "Member"], ["inject", "Inject"]]} testid="filter-source" />
    <FilterSelect label="Diproses oleh" value={f.staff} onChange={(v) => set("staff", v)} options={staff.map((s) => [s, s])} testid="filter-staff" />
    <FilterInput label="Cari ID / WA" value={f.q} onChange={(e) => set("q", e.target.value.replace(/\D/g, "").slice(0, 15))} inputMode="numeric" placeholder="Angka saja" testid="filter-q" />
    <button onClick={onReset} className="rk-btn-ghost inline-flex items-center gap-1 !py-2 text-xs" data-testid="filter-reset"><RotateCcw size={13} /> Reset</button>
  </div>
);

export const HistoryPanel = () => {
  const { can } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const params = cleanParams(f);
  const key = JSON.stringify(params);

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      api.get("/admin/history", { params: { ...JSON.parse(key), page } }).then((r) => live && setData(r.data))
        .catch((e) => live && !isSessionError(e) && toast.error(errMsg(e)));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [key, page]);

  const set = (k, v) => {
    setF((x) => ({ ...x, [k]: v }));
    setPage(1);
  };

  return (
    <section className="space-y-5" data-testid="history-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-xl text-xs text-zinc-500">Log permanen semua transaksi yang diproses, di-inject, atau dihapus otomatis. Data tidak bisa diubah atau dihapus. Waktu dalam WIB.</p>
        {can("history", "manage") ? <ExportButton url="/admin/history/export" params={params} testid="history-export" /> : null}
      </div>
      <Filters f={f} set={set} staff={data?.staff_options || []} onReset={() => { setF(EMPTY); setPage(1); }} />
      {data ? <Summary s={data.summary} /> : null}
      <div className="overflow-x-auto rounded-2xl border border-white/5" data-testid="history-table">
        <table className="w-full text-sm">
          <thead className="bg-[#13131a] text-[11px] uppercase tracking-widest text-zinc-500">
            <tr><Th>Waktu Diisi Member</Th><Th>Waktu Diproses</Th><Th>Jenis</Th><Th>Game</Th><Th>ID Pemain</Th><Th>Nickname</Th><Th>WA</Th><Th>Chip</Th><Th>Nominal</Th><Th>Status</Th><Th>Sumber</Th><Th>Oleh</Th></tr>
          </thead>
          <tbody>
            {!data ? null : data.items.length === 0 ? (
              <tr><td colSpan={12} className="p-8 text-center text-zinc-500" data-testid="history-empty">Tidak ada data sesuai filter.</td></tr>
            ) : data.items.map((h) => (
              <tr key={h.id} className="border-t border-white/5 bg-[#0d0d12]" data-testid={`history-row-${h.id}`}>
                <Td className="whitespace-nowrap text-sky-300" data-testid={`history-created-${h.id}`}>{h.tx_created_at ? fmtWIB(h.tx_created_at) : "—"}</Td>
                <Td className="whitespace-nowrap text-emerald-300" data-testid={`history-processed-${h.id}`}>{fmtWIB(h.at)}</Td>
                <Td>{KIND[h.kind]}</Td>
                <Td className="whitespace-nowrap">{h.product_name}</Td>
                <Td className="font-mono">{h.game_id}</Td>
                <Td className="whitespace-nowrap text-xs font-semibold text-emerald-400" data-testid={`history-nick-${h.id}`}>{h.nickname ? `✓ ${h.nickname}` : <span className="text-zinc-600">—</span>}</Td>
                <Td className="text-zinc-400">{h.wa_number || "—"}</Td>
                <Td className="whitespace-nowrap text-[#ffd700]">{h.chip_label}</Td>
                <Td className="whitespace-nowrap font-semibold">{formatRupiah(h.amount_rp)}</Td>
                <Td className={`whitespace-nowrap text-xs font-bold ${STATUS_CLS[h.status] || ""}`}>{STATUS[h.status] || h.status}</Td>
                <Td className="text-xs">{h.source === "inject" ? <span className="text-fuchsia-300">Inject</span> : "Member"}</Td>
                <Td className="whitespace-nowrap font-mono text-xs">{h.processed_by}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data ? <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} /> : null}
    </section>
  );
};
